import { Controller } from "@hotwired/stimulus"

// Company Settings → Finances → Cost codes (builder companies, Phelipe
// 2026-10-23): Smart upload with a review step (the file's rows, ticked by
// default; untick what shouldn't come in; say whether the rest of the list
// is archived), add or edit a code in one form, archive or restore with a
// checkbox. Rows change in place; an import reloads, the whole list changed.
export default class extends Controller {
  static targets = ["file", "status", "importModal", "importError", "dropzone", "addModal", "addError", "formTitle", "formButton", "editId",
                    "code", "name", "parent", "unit", "labor", "loose", "empty",
                    "deleteButton", "deleteAllButton", "confirmModal", "confirmTitle", "confirmText", "confirmButton",
                    "reviewModal", "reviewSummary", "reviewRows", "archiveMissing", "archiveLabel", "importButton"]
  static values = { previewUrl: String, importUrl: String, createUrl: String, updateUrl: String, toggleUrl: String,
                    destroyUrl: String, destroyAllUrl: String }

  connect() {
    try {
      const pending = sessionStorage.getItem("cpanel-cost-codes-flash")
      if (pending) { sessionStorage.removeItem("cpanel-cost-codes-flash"); this._toast(pending) }
    } catch (e) { /* private mode */ }
  }

  // ── Smart import: drop the file, watch the AI read it, review, import ──

  openImport() {
    this._setImportError("")
    this._status("")
    this.importModalTarget.classList.remove("hidden")
  }

  closeImport() {
    if (this._reading) return // the read is running; the banner says so
    this.importModalTarget.classList.add("hidden")
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-brand", "bg-blue-50")
  }

  dragleave() {
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50")
    const file = event.dataTransfer?.files?.[0]
    if (file) this._read(file)
  }

  pickFile() { this.fileTarget.click() }

  upload() {
    const file = this.fileTarget.files[0]
    if (file) this._read(file)
  }

  async _read(file) {
    if (this._reading) return
    this._reading = true
    this._setImportError("")
    this._startAiProgress()
    const body = new FormData()
    body.append("file", file)
    try {
      const resp = await fetch(this.previewUrlValue, { method: "POST", headers: { "X-CSRF-Token": this._csrf, Accept: "application/json" }, body })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the file")
      this._stopAiProgress()
      this._status("")
      this.importModalTarget.classList.add("hidden")
      this._openReview(data, file.name)
    } catch (e) {
      this._stopAiProgress()
      this._status("")
      this._setImportError(e.message)
    } finally {
      this._reading = false
      this.fileTarget.value = ""
    }
  }

  // The Build SOV banner: rainbow "ConsLog AI — …" with a timer and a stage
  // message that moves on, so a long read never looks stuck.
  _startAiProgress() {
    this._aiStart = performance.now()
    const stages = [
      [0,  "Reading your file..."],
      [6,  "Finding the cost codes..."],
      [15, "Reading divisions, units and labor marks..."],
      [30, "Matching against your list..."],
      [45, "Still working — bigger file, almost there..."]
    ]
    const render = () => {
      const secs = Math.round((performance.now() - this._aiStart) / 1000)
      let msg = stages[0][1]
      for (const [at, text] of stages) { if (secs >= at) msg = text }
      this.statusTarget.innerHTML =
        `<span class="font-bold ai-rainbow-text">ConsLog AI — ${msg}</span> ` +
        `<span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${secs}s</span>`
    }
    render()
    clearInterval(this._aiTimer)
    this._aiTimer = setInterval(render, 1000)
  }

  _stopAiProgress() {
    clearInterval(this._aiTimer)
    this._aiTimer = null
  }

  _setImportError(text) {
    if (this.hasImportErrorTarget) this.importErrorTarget.textContent = text
  }

  _openReview(data, filename) {
    const esc = this._esc
    this._review = data
    const counts = data.counts || {}
    const bits = []
    if (counts.new) bits.push(`${counts.new} new`)
    if (counts.exists) bits.push(`${counts.exists} already in the list (updated)`)
    if (counts.possible) bits.push(`${counts.possible} look like a code you have`)
    if (counts.repeat) bits.push(`${counts.repeat} repeated in the file`)
    if (counts.invalid) bits.push(`${counts.invalid} unusable`)
    this.reviewSummaryTarget.textContent = `${filename}: ${data.items.length} rows${bits.length ? " · " + bits.join(" · ") : ""}${data.source === "ai" ? " · read by AI" : ""}`
    const what = (it) => {
      switch (it.status) {
        case "new": return `<span class="text-green-700">New</span>`
        case "exists": return `<span class="text-gray-600">Updates the code you have</span>`
        case "possible": return `<span class="text-amber-700">Looks like ${esc(it.match?.key || "")}${it.match?.name ? " · " + esc(it.match.name) : ""}</span>`
        case "repeat": return `<span class="text-gray-400">Repeated in the file</span>`
        default: return `<span class="text-red-600">No usable code</span>`
      }
    }
    this.reviewRowsTarget.innerHTML = data.items.map((it, i) => {
      const x = it.extra || {}
      const usable = it.status !== "invalid" && it.status !== "repeat"
      const division = x.parent_code && x.parent_code.toUpperCase() !== "ALL" ? x.parent_code : ""
      return `<tr class="border-t border-gray-100 ${usable ? "" : "text-gray-400"}" data-i="${i}">
        <td class="px-3 py-1.5"><input type="checkbox" class="accent-brand cursor-pointer" data-review-tick ${usable ? "checked" : "disabled"}></td>
        <td class="px-3 py-1.5 tabular-nums">${esc(it.key)}</td>
        <td class="px-3 py-1.5">${esc(it.name)}</td>
        <td class="px-3 py-1.5 text-gray-500">${esc(division)}</td>
        <td class="px-3 py-1.5 text-gray-500">${esc(x.unit || "")}</td>
        <td class="px-3 py-1.5 text-center text-gray-500">${x.labor && /^(x|yes|y|true|1|labou?r)$/i.test(x.labor) ? "✓" : ""}</td>
        <td class="px-3 py-1.5 text-center text-gray-500">${x.active && /^(no|n|false|0|inactive|archived)$/i.test(x.active) ? "No" : "Yes"}</td>
        <td class="px-3 py-1.5">${what(it)}</td>
      </tr>`
    }).join("")
    const missing = data.missing || []
    this.archiveMissingTarget.checked = false
    this.archiveMissingTarget.disabled = missing.length === 0
    this.archiveLabelTarget.textContent = missing.length
      ? `Archive the ${missing.length} active code${missing.length === 1 ? "" : "s"} that aren't in this file`
      : "Every active code you have is in this file"
    this.reviewModalTarget.classList.remove("hidden")
  }

  tickAll(event) {
    this.reviewRowsTarget.querySelectorAll("[data-review-tick]:not(:disabled)").forEach((box) => { box.checked = event.target.checked })
  }

  closeReview() { this.reviewModalTarget.classList.add("hidden") }

  async importSelected() {
    if (!this._review) return
    const items = []
    const keepKeys = []
    this.reviewRowsTarget.querySelectorAll("tr").forEach((row) => {
      const it = this._review.items[Number(row.dataset.i)]
      const box = row.querySelector("[data-review-tick]")
      if (box?.checked) items.push({ key: it.key, name: it.name, extra: it.extra || {} })
      else if (it.status === "exists" || it.status === "possible") keepKeys.push(it.key)
    })
    if (!items.length) { this._toast("Nothing ticked", "error"); return }
    this.importButtonTarget.disabled = true
    try {
      const resp = await fetch(this.importUrlValue, {
        method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, Accept: "application/json" },
        body: JSON.stringify({ items, archive_missing: this.archiveMissingTarget.checked, keep_keys: keepKeys })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Import failed")
      const parts = []
      if (data.created) parts.push(`${data.created} added`)
      if (data.updated) parts.push(`${data.updated} updated`)
      if (data.archived) parts.push(`${data.archived} archived`)
      if (data.skipped) parts.push(`${data.skipped} skipped`)
      this._flashAndReload(`Cost codes imported: ${parts.join(", ") || "nothing changed"}`)
    } catch (e) {
      this.importButtonTarget.disabled = false
      this._toast(e.message, "error")
    }
  }

  // ── Add or edit ─────────────────────────────────────────────────────

  openAdd() {
    this._fillForm({ id: "", code: "", name: "", parent: "", unit: "", labor: false })
    this.formTitleTarget.textContent = "Add a cost code"
    this.formButtonTarget.textContent = "Add"
    this.deleteButtonTarget.classList.add("hidden")
    this._showForm()
  }

  openEdit(event) {
    const d = event.currentTarget.dataset
    this._fillForm({ id: d.id, code: d.code, name: d.name, parent: d.parent, unit: d.unit, labor: d.labor === "true" })
    this.formTitleTarget.textContent = `Edit ${d.code}`
    this.formButtonTarget.textContent = "Save"
    this.deleteButtonTarget.classList.remove("hidden")
    this._showForm()
  }

  _fillForm({ id, code, name, parent, unit, labor }) {
    this.editIdTarget.value = id || ""
    this.codeTarget.value = code || ""
    this.nameTarget.value = name || ""
    this.parentTarget.value = parent || ""
    this.unitTarget.value = unit || ""
    this.laborTarget.checked = Boolean(labor)
  }

  _showForm() {
    this.addErrorTarget.classList.add("hidden")
    this.addModalTarget.classList.remove("hidden")
    this.codeTarget.focus()
  }

  closeAdd() { this.addModalTarget.classList.add("hidden") }
  stopPropagation(event) { event.stopPropagation() }

  async submitAdd() {
    const id = this.editIdTarget.value
    const payload = { code: this.codeTarget.value, name: this.nameTarget.value, parent_code: this.parentTarget.value,
                      unit: this.unitTarget.value, labor: this.laborTarget.checked }
    const url = id ? `${this.updateUrlValue}?id=${encodeURIComponent(id)}` : this.createUrlValue
    try {
      const resp = await fetch(url, {
        method: id ? "PUT" : "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, Accept: "application/json" },
        body: JSON.stringify(payload)
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save the code")
      if (id) this._updateRow(data)
      else this._insertRow(data)
      this.closeAdd()
      this._toast(id ? `${data.code} saved` : `${data.code} added`)
    } catch (e) {
      this.addErrorTarget.textContent = e.message
      this.addErrorTarget.classList.remove("hidden")
    }
  }

  async toggle(event) {
    const box = event.target
    const id = box.dataset.id
    try {
      const resp = await fetch(`${this.toggleUrlValue}?id=${encodeURIComponent(id)}`, {
        method: "PUT", headers: { "X-CSRF-Token": this._csrf, Accept: "application/json" }
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save")
      box.checked = data.active
      const row = box.closest("tr")
      if (row) row.classList.toggle("text-gray-400", !data.active)
      this._toast(data.active ? `${data.code} restored` : `${data.code} archived`)
    } catch (e) {
      box.checked = !box.checked
      this._toast(e.message, "error")
    }
  }

  // ── The lock (S2-10): only codes from this list on the SOV ──────────

  async toggleLock(event) {
    const box = event.target
    try {
      const resp = await fetch(event.params.settingsUrl, {
        method: "PUT", headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, Accept: "application/json" },
        body: JSON.stringify({ sov_codes_from_list: box.checked })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save")
      this._toast(data.sov_codes_from_list ? "The SOV now flags codes that aren't in this list" : "The SOV takes any code again")
    } catch (e) {
      box.checked = !box.checked
      this._toast(e.message, "error")
    }
  }

  // ── Delete: one code from the edit form, or the whole list ─────────

  confirmDeleteOne() {
    const code = this.codeTarget.value || "this code"
    this._confirm = { kind: "one", id: this.editIdTarget.value, code }
    this.confirmTitleTarget.textContent = `Delete ${code}?`
    this.confirmTextTarget.textContent = "It leaves the list for good. Old time entries that used it keep it."
    this.confirmButtonTarget.textContent = "Delete"
    this.confirmModalTarget.classList.remove("hidden")
  }

  confirmDeleteAll() {
    const n = this.element.querySelectorAll("tr[data-id]").length
    this._confirm = { kind: "all" }
    this.confirmTitleTarget.textContent = "Delete the whole list?"
    this.confirmTextTarget.textContent = `All ${n} cost codes leave the list for good. Old time entries that used them keep them. You can import a new list afterwards.`
    this.confirmButtonTarget.textContent = "Delete everything"
    this.confirmModalTarget.classList.remove("hidden")
  }

  closeConfirm() { this.confirmModalTarget.classList.add("hidden") }

  async confirmed() {
    const c = this._confirm
    if (!c) return
    this.confirmButtonTarget.disabled = true
    try {
      if (c.kind === "one") {
        const resp = await fetch(`${this.destroyUrlValue}?id=${encodeURIComponent(c.id)}`, {
          method: "DELETE", headers: { "X-CSRF-Token": this._csrf, Accept: "application/json" }
        })
        const data = await resp.json()
        if (!resp.ok) throw new Error(data.error || "Couldn't delete")
        this.closeConfirm()
        this.closeAdd()
        this.element.querySelector(`tr[data-id="${c.id}"]`)?.remove()
        this._toast(`${c.code} deleted`)
      } else {
        const resp = await fetch(this.destroyAllUrlValue, {
          method: "DELETE", headers: { "X-CSRF-Token": this._csrf, Accept: "application/json" }
        })
        const data = await resp.json()
        if (!resp.ok) throw new Error(data.error || "Couldn't delete")
        this._flashAndReload(`Cost codes: ${data.deleted || 0} deleted`)
      }
    } catch (e) {
      this._toast(e.message, "error")
    } finally {
      this.confirmButtonTarget.disabled = false
    }
  }

  // ── Rows ────────────────────────────────────────────────────────────

  _rowHTML(c) {
    const esc = this._esc
    return `<tr class="border-t border-gray-100 group ${c.active ? "" : "text-gray-400"}" data-id="${c.id}" data-search-row data-search-text="${esc(`${c.code} ${c.name} ${c.unit || ""}`)}">
      <td class="px-3 py-1.5 tabular-nums ${c.parent_code ? "pl-7" : ""}" data-cell="code">${esc(c.code)}</td>
      <td class="px-3 py-1.5" data-cell="name">${esc(c.name)}</td>
      <td class="px-3 py-1.5 text-gray-500" data-cell="unit">${esc(c.unit)}</td>
      <td class="px-3 py-1.5 text-center text-gray-500" data-cell="labor">${c.labor ? "✓" : ""}</td>
      <td class="px-3 py-1.5 text-center"><input type="checkbox" class="accent-brand cursor-pointer" ${c.active ? "checked" : ""} data-id="${c.id}" data-action="change->cpanel-cost-codes#toggle" title="Active"></td>
      <td class="px-2 py-1.5 text-right">
        <button type="button" class="p-1 rounded text-gray-300 group-hover:text-gray-500 hover:bg-gray-100 cursor-pointer" title="Edit"
                data-action="click->cpanel-cost-codes#openEdit"
                data-id="${c.id}" data-code="${esc(c.code)}" data-name="${esc(c.name)}" data-parent="${esc(c.parent_code)}" data-unit="${esc(c.unit)}" data-labor="${c.labor}">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z"/><path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z"/></svg>
        </button>
      </td>
    </tr>`
  }

  // A new code goes under its division when that division is on the page,
  // else with the other codes.
  _insertRow(c) {
    const tbody = this._tbodyFor(c)
    if (!tbody) { this._flashAndReload(`${c.code} added`); return }
    if (this.hasEmptyTarget) this.emptyTarget.remove()
    tbody.insertAdjacentHTML("beforeend", this._rowHTML(c))
  }

  // An edited code is redrawn where it is; one that changed division moves.
  _updateRow(c) {
    const row = this.element.querySelector(`tr[data-id="${c.id}"]`)
    if (!row) { this._flashAndReload(`${c.code} saved`); return }
    const tbody = this._tbodyFor(c)
    const moved = tbody && row.parentElement !== tbody
    if (moved) {
      // A division row with children, or a code leaving its group: the page
      // draws the groups, so let it.
      this._flashAndReload(`${c.code} saved`)
      return
    }
    row.outerHTML = this._rowHTML(c)
  }

  _tbodyFor(c) {
    const group = c.parent_code && this.element.querySelector(`tbody[data-division="${CSS.escape(c.parent_code)}"]`)
    return group || (this.hasLooseTarget ? this.looseTarget : null)
  }

  // ── Small helpers ───────────────────────────────────────────────────

  _status(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  get _csrf() { return document.querySelector("meta[name='csrf-token']")?.content }

  get _esc() {
    return (v) => String(v ?? "").replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]))
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "error" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }

  _flashAndReload(message) {
    try { sessionStorage.setItem("cpanel-cost-codes-flash", message) } catch (e) { /* private mode */ }
    window.location.reload()
  }
}
